import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../hooks/use-stores';
import EmptyItem from '../Home/EmptyItem';
import { FlexDiv, palette } from '@awarego-ui/components';
import styled from 'styled-components';
import OneMomentSpinner from '../../components/OneMomentSpinner';
import TrainingsListHeader from '../../components/training/trainings-list-header';
import TrainingsListDetails from '../../components/training/trainings-list-details';

const StyledFlexDiv = styled(FlexDiv)`
    transition: 0.4s ease-out;

    padding: 24px;
    border-radius: 4px;
    @media (max-width: 992px) {
        padding: 16px;
    }

    &:hover {
        box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08),
            0px 4px 24px rgba(29, 30, 29, 0.08);
    }
`;

const ExpiredFlexDiv = styled(StyledFlexDiv)`
    border-left: 3px solid ${palette.vibrant.red};
    opacity: 0.85;
`;

const sortByCreated = (a, b) => new Date(b.created) - new Date(a.created);

function TrainingCard({ training, brandingStore, firstExpired }) {
    const Wrapper = training.expired ? ExpiredFlexDiv : StyledFlexDiv;
    return (
        <Wrapper
            gap={24}
            as={Link}
            to={`/trainings/${training.id}`}
            mt={firstExpired ? 32 : undefined}
        >
            <TrainingsListHeader training={training} />
            <TrainingsListDetails
                training={training}
                brandingStore={brandingStore}
            />
        </Wrapper>
    );
}

function TrainingsList() {
    const { t } = useTranslation();
    const { trainingStore, store, brandingStore } = useStores();
    const { loadingData, trainings } = trainingStore;

    useEffect(() => {
        store.setLayoutTitle(t('Awareness and Compliance Training'));
    }, [store, t]);

    const { active, pastDue } = useMemo(() => {
        const visible = trainings
            .slice()
            .filter((tr) => tr.active !== -1)
            .sort(sortByCreated);
        return {
            active: visible.filter((tr) => !tr.expired),
            pastDue: visible.filter((tr) => tr.expired),
        };
    }, [trainings]);

    if (loadingData) return <OneMomentSpinner />;

    if (trainings.length === 0) return <EmptyItem item="training" />;

    return (
        <FlexDiv column gap={8}>
            {active.map((training) => (
                <TrainingCard
                    key={training.id}
                    training={training}
                    brandingStore={brandingStore}
                />
            ))}

            {pastDue.map((training, i) => (
                <TrainingCard
                    key={training.id}
                    training={training}
                    brandingStore={brandingStore}
                    firstExpired={i === 0 && active.length > 0}
                />
            ))}
        </FlexDiv>
    );
}

export default observer(TrainingsList);
